import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserRole } from "@/store/authStore";
import { holdsAnyRole } from "@/lib/roles";
import published from "./releaseNotes.json";

/**
 * One entry in What's new.
 *
 * `releaseNotes.json` is drafted from the log of `main` by
 * `scripts/releaseNotes.ts` and edited by hand in review before it merges -
 * that review is the only gate between a commit subject and a teacher's
 * screen, so the file is the published notes, not a cache of the log.
 */
export interface ReleaseNote {
  sha: string;
  pr: number | null;
  /** `YYYY-MM-DD`: the day it reached production. */
  date: string;
  type: string;
  text: string;
  /**
   * Who it is news to. Absent means everyone. Set by the reviewer, never by
   * the generator: an attendance report is news to the office and noise to a
   * parent, and only a person can tell which notes are which.
   */
  audience?: UserRole[];
}

/** Newest first, as the generator writes them. */
export const RELEASE_NOTES: ReleaseNote[] = published.notes as ReleaseNote[];

export function notesFor(notes: ReleaseNote[], roles: readonly UserRole[]): ReleaseNote[] {
  return notes.filter((note) => !note.audience || holdsAnyRole(roles, note.audience));
}

/** Where someone had read up to: the newest note in the feed then, and its day. */
export interface SeenMark {
  sha: string;
  date: string;
}

/**
 * What this person has not seen yet: every note newer than `seen`, within
 * their audience.
 *
 * `seen` is the newest note in the *whole* feed when they last looked, not the
 * newest one shown to them, so a note hidden from them still marks the point
 * they had read up to. If that note has since been removed from the feed, the
 * day it shipped still places them: anything from a later day is new.
 */
export function unreadNotes(
  notes: ReleaseNote[],
  roles: readonly UserRole[],
  seen: SeenMark | undefined,
): ReleaseNote[] {
  if (!seen) return notesFor(notes, roles);
  const seenAt = notes.findIndex((note) => note.sha === seen.sha);
  const newer = seenAt === -1 ? notes.filter((note) => note.date > seen.date) : notes.slice(0, seenAt);
  return notesFor(newer, roles);
}

interface SeenState {
  /** Per user id, the newest note in the feed when they last opened What's new. */
  seen: Record<string, SeenMark>;
  markSeen: (userId: string, mark: SeenMark) => void;
}

/**
 * Which notes each person has read, in this browser.
 *
 * Local rather than on the server because losing it costs nothing worth a
 * table: a cleared browser shows the dot again, and opening the page clears it.
 * Keyed by user so a shared office computer doesn't tell one clerk they've
 * read what another opened.
 */
export const useReleaseNotesSeen = create<SeenState>()(
  persist(
    (set) => ({
      seen: {},
      markSeen: (userId, mark) => set((state) => ({ seen: { ...state.seen, [userId]: mark } })),
    }),
    { name: "gradebook.releaseNotes.seen" },
  ),
);
